import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormTextField } from '@skupreme/shared/ui/form';
import { ErrorMessageConstant } from '@utility/constants/common';
import OAuthService from '@utility/services/oauth';
import {
  ConnectIntro,
  ConnectPopup,
  failureText,
  type ConnectMarketplaceItem,
} from './ConnectPopup';

const walmartSchema = z.object({
  client_id: z.string().min(1, 'Client Id is required!'),
  client_secret: z.string().min(1, 'Client secret is required!'),
});

type WalmartValues = z.infer<typeof walmartSchema>;

const EMPTY: WalmartValues = { client_id: '', client_secret: '' };

type WalmartConnectPopupProps = {
  item: ConnectMarketplaceItem | null;
  open: boolean;
  closeModal: (success?: boolean) => void;
};

export function WalmartConnectPopup({
  item,
  open,
  closeModal,
}: WalmartConnectPopupProps) {
  const { control, handleSubmit, reset, clearErrors } = useForm<WalmartValues>({
    resolver: zodResolver(walmartSchema),
    defaultValues: EMPTY,
    shouldUnregister: true,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const close = (connected?: boolean) => {
    reset(EMPTY);
    clearErrors();
    setSuccess('');
    setError('');
    closeModal(connected);
  };

  const onSubmit = async (data: WalmartValues) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await OAuthService.walmartIntegrate({
        marketplace_id: item?.marketplace_id,
        marketplace_region_id: item?.marketplace_region_id,
        ...data,
      });
      setSuccess('Walmart integrated successfully');
      setTimeout(() => {
        close(true);
      }, 2000);
    } catch (failure) {
      setError(
        failureText(failure, 'error', ErrorMessageConstant.defaultAPIError),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConnectPopup
      open={open}
      title={`${item?.alias ?? ''} Integration`}
      onClose={() => close()}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Submit"
      isSubmitting={saving}
      error={error}
      success={success}
    >
      <ConnectIntro logo="/images/marketplaces/walmart.svg">
        Please paste your Walmart API credentials here
      </ConnectIntro>
      <FormTextField
        control={control}
        name="client_id"
        label="Client Id"
        isRequired
      />
      <FormTextField
        control={control}
        name="client_secret"
        label="Client Secret"
        isRequired
      />
    </ConnectPopup>
  );
}
